import React, {
    ReactElement, useState,
} from "react";
import {
    GetHubSpokeStatusDocument,
    HubSpokeStatusForward,
    useDisableHubSpokeMutation,
    useEnableHubSpokeMutation, useGetHubMetadataLazyQuery, useGetHubSpokeStatusQuery, useGetHubSpokeTokenLazyQuery,
} from "../../../../../__generated__/api";
import CodeBlock from "../../../../../components/code-block/CodeBlock";
import Confirm from "../../../../../components/confirm/Confirm";
import Help from "../../../../../components/help/Help";
import { LoadingIcon } from "../../../../../components/loading/Loading";
import Alert from "../../../../../components/theme/alert/Alert";
import Button from "../../../../../components/theme/button/Button";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../../components/theme/settings";
import useArticleContext from "../../../../../contexts/HelpContext";
import Styles from "./Forwarding.module.scss";
import createTableContainer from "../../../../../components/theme/table";
import Text from "../../../../../components/theme/text/Text";
import useDeleteConfirm from "../../../../../components/confirm/Delete";

const TableContainer = createTableContainer<HubSpokeStatusForward>();

export function HubAndSpoke({
    shouldShow,
    onHide,
}: {
    shouldShow: boolean,
    onHide: () => void,
}): ReactElement {
    const [remoteToken, setRemoteToken] = useState("");
    const [getTokenData, { data: tokenData, loading: isTokenDataLoading }] = useGetHubMetadataLazyQuery();
    const [enableHubAndSpoke] = useEnableHubSpokeMutation();

    const loadToken = async () => {
        await getTokenData({
            variables: {
                token: remoteToken.trim(),
            },
        });
    };

    const confirmToken = async () => {
        await enableHubAndSpoke({
            refetchQueries: [
                { query: GetHubSpokeStatusDocument },
            ],
            variables: {
                settings: {
                    destinationToken: remoteToken.trim(),
                },
            },
        });
        onHide();
    };

    return (
        <Confirm
            shouldShow={shouldShow}
            onConfirm={tokenData ? confirmToken : loadToken}
            confirmText={tokenData ? "Confirm" : "Next"}
            onHide={onHide}
            title="Hub Token"
        >
            <div>
                {tokenData ? (
                    <div>
                        <p>Do you want to forward all messages from this workspace to the following Foqal Workspace</p>
                        <Alert variant="primary">
                            {tokenData.getHubData?.name}
                        </Alert>

                    </div>
                ) : (
                    <div>
                        {isTokenDataLoading ? <LoadingIcon /> : (
                            <>
                                <FormGroup
                                    display="textarea"
                                    label="Please enter the Hub Token from the other workspace."
                                    placeholder="Add a Hub Token"
                                    value={remoteToken}
                                    groupClassName={Styles["hub-token-input"]}
                                    onChange={event => setRemoteToken(event.target.value)}
                                />
                                <Help
                                    articleId="urn:fq:article:3c07322acde14bea8b0a5e480075acb0"
                                    buttonType="small-icon"
                                    button="Where I can find a Hub Token?"
                                    buttonClassName={Styles["help-button"]}

                                />
                            </>

                        )}

                    </div>
                )}

            </div>
        </Confirm>
    );
}
export default function Forwarding(): ReactElement {
    useArticleContext("urn:fq:article:3c07322acde14bea8b0a5e480075acb0");

    const [shouldStartForwarding, setShouldStartForwarding] = useState(false);

    const { data, loading: isLoading } = useGetHubSpokeStatusQuery();

    const [getToken, { data: tokenData, loading: isTokenLoading }] = useGetHubSpokeTokenLazyQuery();
    const [disableHubSpoke] = useDisableHubSpokeMutation();

    const { element: disableHubSpokeElement, setDeleting: setDisableHubSpoke } = useDeleteConfirm(
        async () => {
            await disableHubSpoke({
                refetchQueries: [
                    { query: GetHubSpokeStatusDocument },
                ],
                variables: {
                    settings: {},
                },
            });
        },
        () => data?.getHubSpokeStatus?.forwardingTo?.name,
        "Stop Forwarding",
        "Are you sure you want to stop forwarding messages to the workspace",
        "Confirm",
    );

    return (
        <>

            <SettingsContainer navigation="none">
                <SettingsContainer.Section
                    title="Hub & Spoke"
                    helpText="Hub and spoke allows you to forward messages between multiple
                        Slack workspaces, channels, or sources. in one Foqal Agent workspace,
                        use Hub and Spoke to help. Meaning, your customer can send a message
                        in one workspaces, and your agents can respond in another. To get
                        started, select whether this Foqal Agent Workspace is the Hub
                        or the Spoke."
                >
                    {data?.getHubSpokeStatus?.forwardingTo ? (
                        <SettingsContainer.Setting
                            title="Hub Configuration"
                            description="The Hub is the parent workspace that receives messages
                                from all connected spokes. This is where your agents work from and
                                manage communications."
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={isLoading}
                        >
                            <Text as="p" textVariant="body-1-regular">
                                Hub and Spoke is enabled in this workspace. All messages are being forwarded to
                                {" "}
                                {data?.getHubSpokeStatus?.forwardingTo.name}
                            </Text>
                            <Button type="button" variant="danger" onClick={() => setDisableHubSpoke({})}>
                                Disable
                            </Button>
                        </SettingsContainer.Setting>
                    ) : (
                        <>
                            <SettingsContainer.Setting
                                title="Hub Configuration"
                                description="The Hub is the parent workspace that receives messages
                                from all connected spokes. This is where your agents work from and
                                manage communications."
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={isLoading}
                            >
                                {tokenData?.hubSpokeToken?.token ? (
                                    <CodeBlock shouldWrap code={tokenData?.hubSpokeToken?.token} />
                                ) : (
                                    <div>
                                        {isTokenLoading ? (
                                            <LoadingIcon />
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="primary"
                                                onClick={() => getToken()}
                                            >
                                                Generate Hub Token
                                            </Button>
                                        )}
                                    </div>
                                )}

                            </SettingsContainer.Setting>
                            <SettingsContainer.Setting
                                title="Spoke Configuration"
                                description="Spokes are child workspaces that forward conversations to the Hub.
                            This is typically where your customers or external teams communicate."
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={isLoading}
                            >
                                <Button
                                    size="small"
                                    variant="primary"
                                    onClick={() => setShouldStartForwarding(true)}
                                >
                                    Forward to Hub
                                </Button>

                            </SettingsContainer.Setting>
                        </>
                    )}

                </SettingsContainer.Section>
                <SettingsContainer.Section
                    title="Currently Forwarded From"
                    helpText="The list below shows all of the workspaces that are
                        currently forwarding to this workspace."
                >
                    <SettingsContainer.Setting
                        title="Hub & Spoke Current Forwarding"
                        description="Currently forwarded list of channels being forwarded to this
                            workspace."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isHideTitle
                        isVisible={data?.getHubSpokeStatus?.forwardedFrom?.isNotEmpty}
                    >
                        <TableContainer
                            data={data?.getHubSpokeStatus?.forwardedFrom ?? []}
                            isLoading={isLoading}
                            borderVariant="secondary"
                        >
                            <TableContainer.TableControls>
                                <TableContainer.TableControls.Search
                                    search={(query, item) => item.name?.toLocaleLowerCase().includes(query)}
                                />
                                <TableContainer.TableControls.Seperator />

                                <TableContainer.TableControls.SortBy />
                            </TableContainer.TableControls>
                            <TableContainer.Table>
                                <TableContainer.TableValue
                                    columnKey="name"
                                    title="Name"
                                    sortBy={item => item.name?.toLowerCase()}
                                >
                                    {item => item.name}
                                </TableContainer.TableValue>

                            </TableContainer.Table>
                        </TableContainer>
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>

            </SettingsContainer>
            <HubAndSpoke shouldShow={shouldStartForwarding} onHide={() => setShouldStartForwarding(false)} />
            {disableHubSpokeElement}
        </>
    );
}
